import { Alert, Button, Form, Modal } from "react-bootstrap";
import { LoadingIcon } from "../../../Loading";
import { useEffect, useRef, useState } from "react";
import { hasSpecialCharacter } from "../../../FileDrive/filedriveFuncs";

type UploadModalProps = {
    showModal: boolean,
    setShowModal: (v: boolean) => void,
    handleBuyerDocUpload: (docFullName: string) => Promise<void>,
    file: File,
    setFile:  (f: File) => void,
    uploadDocLoading: boolean,
    docUploadName: string,
    setDocToUpload: (arg0: {name: string, ext: string}) => void,
}

const UploadModal = ({
    showModal,
    setShowModal,
    handleBuyerDocUpload,
    file,
    setFile,
    uploadDocLoading,
    docUploadName,
    setDocToUpload,
}: UploadModalProps) => {
    const FormSubmitRef = useRef(null);
    const [validated, setValidated] = useState<boolean>(false);
    const [fileSizeError, setFileSizeError] = useState<string>("");
    const fileSpecialChars: string[] = ["\\", "/", "<", ">", ".", "{", "}", "\"", "`"];

    useEffect(() => {
        setValidated(docUploadName > '' && !hasSpecialCharacter(docUploadName, fileSpecialChars));
    }, [docUploadName]);

    const DocSpecialCharactersWarning = () => (
        <Alert variant="primary">Document name can not contain special characters eg. <b>{fileSpecialChars}</b>.</Alert>
    );

    const handleDocSubmit = (event) => {
        try {
            event.preventDefault();
            event.stopPropagation();
            if (validated) {
                handleBuyerDocUpload(docUploadName);
            }
        } catch (e) {
            console.log(e);
            event.preventDefault();
            event.stopPropagation();
        }
    }

    const checkFileSize = (file: File): boolean => {
        const MAX_FILE_SIZE = 25 * Math.pow(2, 20); // 25 MB
        if (file.size > MAX_FILE_SIZE) {
            setValidated(false);
            return false;
        }
        return true;
    }

    const handleUploadReset = () => {
        setValidated(false);
        setFileSizeError("");
        setFile(null);
        setDocToUpload({name: null, ext: null});
    }

    const uploadNewDocChange = (e) => {
        // setValidated(true);

        const selectedFile: File = file ?? ((e.target) as any).files?.[0];
        if (!selectedFile) return;

        if (!checkFileSize(selectedFile)) {
            // Drop the oversize file so the picker stays visible, and tell
            // the user why nothing was uploaded.
            handleUploadReset();
            if ((e.target as HTMLInputElement).type === "file") {
                (e.target as HTMLInputElement).value = "";
            }
            setFileSizeError(`"${selectedFile.name}" exceeds the upload limit of 25MB. Please try another file.`);
            return;
        }
        setFileSizeError("");

        let filename = "";
        if (file) {
            filename = e.currentTarget.fileToUploadName?.value;
            const docExt = e.currentTarget.fileToUploadExt?.value;
            setDocToUpload({name: filename, ext: docExt});
        } else {
            const name: string = ((e.target) as any).files[0].name;
            filename = name.substring(0, name.indexOf("."));
            let ext = name.substring(name.indexOf('.') + 1);
            setDocToUpload({name: filename, ext: ext});
        }
    }

    return (<>
    <Modal centered show={showModal} size="sm">
            <Modal.Header>
                <Modal.Title>Upload Document</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {uploadDocLoading ?
                    <LoadingIcon showMsg={true} extraMsg="Uploading Document" inline={true} />
                    : 
                    <Form noValidate validated={validated}
                        onSubmit={handleDocSubmit}
                        autoComplete="off"
                        onChange={uploadNewDocChange}>
                        {fileSizeError > "" && <Alert variant="danger">{fileSizeError}</Alert>}
                        {file ?
                            <>
                                <DocSpecialCharactersWarning />
                                <Form.Label>File Name</Form.Label>
                                <Form.Group>
                                    <Form.Control required type="text" id="fileToUploadName"
                                        placeholder="please enter file name.."
                                        autoFocus
                                        defaultValue={file.name.substring(0, file.name.indexOf("."))} />
                                    <Form.Control.Feedback type="invalid">Please fill in the new document name</Form.Control.Feedback>
                                    <Form.Control disabled id="fileToUploadExt"
                                        defaultValue={file.name.substring(file.name.indexOf(".") + 1)} />
                                </Form.Group>
                            </>
                            :
                            <Form.Group controlId="fileUploader">
                                <Form.Control onChange={(e) => { setFile(((e.target) as any).files[0]) }}
                                    className="form-upload" type="file" />
                                <Form.Control.Feedback type="invalid">Please select file to upload </Form.Control.Feedback>
                            </Form.Group>
                        }

                        <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                    </Form>
                }
            </Modal.Body>
            <Modal.Footer>
                {!uploadDocLoading &&
                    <>
                        <Button variant="text" onClick={() => {setShowModal(false); handleUploadReset();}}>Cancel</Button>
                        {file &&
                            <Button variant="primary" onClick={() => {
                                if (FormSubmitRef.current) {
                                    FormSubmitRef.current.click()
                                }
                            }}>Upload</Button>}
                    </>
                }
            </Modal.Footer>
        </Modal>
    </>)
}

export default UploadModal;